import type { FallbackProps } from "react-error-boundary";

import { useResetErrorBoundaryOnLocationChange, Translate } from "ra-core";
import { CircleAlert, History } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { AppCrashFallback } from "@/components/app/AppCrashFallback";
import { isStaleChunkError } from "@/components/app/staleChunkRecovery";
import type { HtmlHTMLAttributes } from "react";
import { ErrorInfo } from "react";

export const Error = (props: InternalErrorProps & {}) => {
  const { error, errorInfo, resetErrorBoundary, ...rest } = props;
  const readableError = getReadableErrorMessage(error);

  useResetErrorBoundaryOnLocationChange(resetErrorBoundary);

  // Build velho na aba não é erro da aplicação, e esta tela genérica era um beco
  // sem saída: dizia "Um erro ocorreu" com um "Voltar" que não conserta nada.
  // O usuário chega aqui, e não no AppCrashFallback, porque este boundary do
  // react-admin é mais interno e pega o erro da rota lazy primeiro. Foi o que o
  // Rick viu em /#/campanhas depois do deploy de 09/09/2026: o chunk publicado
  // exporta CampanhasPage normalmente, o index.html da aba é que era de antes.
  if (isStaleChunkError(error)) {
    return <AppCrashFallback error={error as Error} />;
  }

  return (
    <div className="flex flex-col items-center md:p-16 gap-5" {...rest}>
      <h1 className="flex items-center text-3xl mt-5 mb-5 gap-3" role="alert">
        <CircleAlert className="w-2em h-2em" />
        <Translate i18nKey="ra.page.error" />
      </h1>
      <div>
        <Translate i18nKey="ra.message.error" />
      </div>
      {readableError && (
        <Accordion
          type="multiple"
          className="mt-1 p-2 bg-secondary w-full lg:w-150"
        >
          <AccordionItem value="error-message">
            <AccordionTrigger className="py-2">
              Detalhes do erro
            </AccordionTrigger>
            <AccordionContent className="whitespace-pre-wrap pt-1 text-sm">
              {readableError}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      {process.env.NODE_ENV !== "production" && (
        <>
          <Accordion
            type="multiple"
            className="mt-1 p-2 bg-secondary w-full lg:w-150"
          >
            <AccordionItem value="error-stack">
              <AccordionTrigger className="py-2">
                Stack do componente
              </AccordionTrigger>
              <AccordionContent className="whitespace-pre-wrap pt-1">
                <pre className="text-xls">{errorInfo?.componentStack}</pre>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <p className="text-center ">
            Need help with this error? Try the following:
          </p>
          <div>
            <ul className="list-disc">
              <li>
                Check the{" "}
                <a
                  className="text-primary underline-offset-4 hover:underline"
                  href="https://marmelab.com/shadcn-admin-kit/docs"
                >
                  shadcn-admin-kit documentation
                </a>
              </li>
              <li>
                Search on{" "}
                <a
                  className="text-primary underline-offset-4 hover:underline"
                  href="https://stackoverflow.com/questions/tagged/shadcn-admin-kit"
                >
                  StackOverflow
                </a>{" "}
                for community answers
              </li>
              <li>
                Get help from the core team via{" "}
                <a
                  className="text-primary underline-offset-4 hover:underline"
                  href="https://marmelab.com/shadcn-admin-kit/"
                >
                  Shadcn Enterprise Edition
                </a>
              </li>
            </ul>
          </div>
        </>
      )}
      <div className="mt-8">
        <Button onClick={goBack}>
          <History />
          <Translate i18nKey="ra.action.back" />
        </Button>
      </div>
    </div>
  );
};

interface InternalErrorProps
  extends Omit<HtmlHTMLAttributes<HTMLDivElement>, "title">,
    FallbackProps {
  className?: string;
  errorInfo?: ErrorInfo;
}

export interface ErrorProps extends Pick<FallbackProps, "error"> {
  errorInfo?: ErrorInfo;
}

function goBack() {
  window.history.go(-1);
}

function getReadableErrorMessage(error: unknown): string | null {
  if (!error) return null;

  const err = error as {
    message?: string;
    status?: number;
    statusText?: string;
    body?: unknown;
  };

  const body = err.body as { message?: string; error?: string; details?: string } | undefined;
  const statusLabel =
    err.status || err.statusText
      ? [err.status, err.statusText].filter(Boolean).join(" ")
      : null;

  const message =
    body?.message || body?.error || body?.details || err.message || null;

  if (statusLabel && message) {
    return `${statusLabel}: ${message}`;
  }

  return statusLabel || message;
}
